// components/CalendarNavbarSpaces.js
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { selectAuthToken } from "store/auth/selectors";
import { createPlaceGroupService } from "services/placeGroupService";
import CompactSpaceCard from "./CompactSpaceCard";
import { FiltersDropdown, NoResultsSection, Tooltip } from "reusableComponents";
import { SettingsIconSvg } from "assets/icons/svgComponents";
import styles from "./styles.module.scss";
import { CTAButton } from "modules/Header/components";

const CalendarNavbarSpaces = ({
  activeSpaceId,
  setActiveSpaceId,
  selectedPlaceGroupId,
  setSelectedPlaceGroupId,
  onShowAllBookings,
  showAllBookings,
}) => {
  const { t } = useTranslation("global");
  const navigate = useNavigate();
  const accessToken = useSelector(selectAuthToken);
  const placeGroupService = createPlaceGroupService(accessToken);

  const [spaces, setSpaces] = useState([]);
  const [placeGroups, setPlaceGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add "All Spaces" option to place groups
  const allSpacesOption = {
    id: "all_spaces",
    label: t("calendar.timetable.allBookings"),
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch place groups
        const groups = await placeGroupService.getGroups();
        const noGroupsOption = {
          id: "%7Bid%7D",
          label: t("calendar.timetable.noGroup"),
        };
        
        // Add "All Spaces" as the first option
        const groupsWithOptions = [allSpacesOption, noGroupsOption, ...groups.data];
        setPlaceGroups(groupsWithOptions);

        // Fetch spaces for display in sidebar based on selected group
        let spacesData;
        
        if (selectedPlaceGroupId === "%7Bid%7D") {
          // Show spaces without groups
          spacesData = await placeGroupService.getWithoutGroupPlaces();
        } else if (selectedPlaceGroupId && selectedPlaceGroupId !== "all_spaces") {
          // Show spaces from selected group
          spacesData = await placeGroupService.getPlaces(selectedPlaceGroupId);
        } else {
          // For "All Spaces" or initial load, fetch all spaces for display
          const allGroupsSpaces = await Promise.all(
            groups.data.map(group => placeGroupService.getPlaces(group.id))
          );
          const noGroupSpaces = await placeGroupService.getWithoutGroupPlaces();
          
          spacesData = {
            data: [
              ...noGroupSpaces.data,
              ...allGroupsSpaces.flatMap(result => result.data)
            ]
          };
        }

        setSpaces(spacesData.data);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [accessToken, selectedPlaceGroupId, t]);

  const handleSpaceClick = (spaceId) => {
    // When clicking a space, we want to filter by that specific space
    // This overrides any group selection
    if (activeSpaceId === spaceId) {
      // Deselect the space
      setActiveSpaceId(null);
      // Reset to group selection mode
      if (selectedPlaceGroupId === "all_spaces") {
        onShowAllBookings && onShowAllBookings(true);
      } else {
        onShowAllBookings && onShowAllBookings(false);
      }
    } else {
      // Select the space
      setActiveSpaceId(spaceId);
      // When a specific space is selected, we don't show all bookings
      onShowAllBookings && onShowAllBookings(false);
    }
  };

  const handleGroupSelect = (groupId) => {
    setSelectedPlaceGroupId(groupId);
    setActiveSpaceId(null); // Clear any selected space when switching groups
    
    if (groupId === "all_spaces") {
      // When "All Spaces" is selected, show ALL bookings (no filtering by space/group)
      onShowAllBookings && onShowAllBookings(true);
    } else {
      // When a specific group is selected, show only bookings from that group
      onShowAllBookings && onShowAllBookings(false);
    }
  };

  return (
    <div className={styles.spacesContainer}>
      <div className={styles.filterRow}>
        <FiltersDropdown
          options={placeGroups.map((group) => ({
            value: group.id,
            label: group.label,
            color: group.color,
          }))}
          selected={selectedPlaceGroupId}
          onSelect={handleGroupSelect}
          placeholder={t("calendar.timetable.allBookings")}
          showColorIndicator
        />
   
        <CTAButton
          variant="light"
          onClick={() => navigate("/calendar/groups")}
          className={styles.settingsButton}
          icon={<SettingsIconSvg />}
          aria-label={t("calendar.groups.manage")}
        >
          {t("calendar.menu.groupsManagement")}
        </CTAButton>
      </div>

      <div className={styles.spacesGrid}>
        {loading ? (
          [...Array(3)].map((_, i) => <div key={i} />)
        ) : spaces.length > 0 ? (
          spaces.map((space) => (
            <CompactSpaceCard
              key={space.id}
              space={space}
              isActive={activeSpaceId === space.id}
              onClick={() => handleSpaceClick(space.id)}
            />
          ))
        ) : (
          <NoResultsSection text={t("calendar.timetable.noSpaceInGroup")} />
        )}
      </div>
    </div>
  );
};

export default CalendarNavbarSpaces;